import { Link } from "react-router-dom";
import { Facebook, Github, Instagram, Linkedin, Mail, MapPin, Phone, Twitter } from "lucide-react";
import { contact, legalLinks, services } from "@/data/site";
import { Wordmark } from "./Header";

function ThreadsIcon({ size = 15 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.01v-.017c.03-3.579.879-6.43 2.525-8.482C5.845 1.205 8.6.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.04.569c-1.104-3.96-3.898-5.984-8.304-6.015-2.91.022-5.11.936-6.54 2.717C4.307 6.504 3.616 8.914 3.589 12c.027 3.086.718 5.496 2.057 7.164 1.43 1.783 3.631 2.698 6.54 2.717 2.623-.02 4.358-.631 5.8-2.045 1.647-1.613 1.618-3.593 1.09-4.798-.31-.71-.873-1.3-1.634-1.75-.192 1.352-.622 2.446-1.284 3.272-.886 1.102-2.14 1.704-3.73 1.79-1.202.065-2.361-.218-3.259-.801-1.063-.689-1.685-1.74-1.752-2.964-.065-1.19.408-2.285 1.33-3.082.88-.76 2.119-1.207 3.583-1.291a13.853 13.853 0 013.02.142c-.126-.742-.375-1.332-.75-1.757-.513-.586-1.308-.883-2.359-.89h-.029c-.844 0-1.992.232-2.721 1.32L8.734 7.847c.98-1.454 2.568-2.256 4.478-2.256h.044c3.194.02 5.097 1.975 5.287 5.388.108.046.216.094.321.142 1.49.7 2.58 1.761 3.154 3.07.797 1.82.871 4.79-1.548 7.158-1.85 1.81-4.094 2.628-8.277 2.65zm1.003-11.128c-.242 0-.487.007-.739.021-1.836.103-2.98.946-2.916 2.143.067 1.256 1.452 1.839 2.784 1.767 1.224-.065 2.818-.543 3.086-3.71a10.5 10.5 0 00-2.215-.221z" />
    </svg>
  );
}

const socials = [
  { label: "LinkedIn", icon: Linkedin, href: "https://www.linkedin.com/in/bytevorait-solutions-528061437/", testId: "social-linkedin" },
  { label: "Facebook", icon: Facebook, href: "https://www.facebook.com/people/bytevoraitsolutions/61594000816005/", testId: "social-facebook" },
  { label: "Instagram", icon: Instagram, href: "https://www.instagram.com/bytevoraitsolutions", testId: "social-instagram" },
  { label: "Threads", icon: ThreadsIcon, href: "https://www.threads.com/@bytevoraitsolutions", testId: "social-threads" },
  { label: "X", icon: Twitter, href: "https://x.com/bytevorait4ohs", testId: "social-x" },
];

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="border-t border-white/[0.08] bg-navy text-white">
      <div className="mx-auto w-full max-w-[1200px] px-6 py-16 md:px-8 md:py-20">
        <div className="grid gap-12 lg:grid-cols-[1.5fr_1fr_1fr_1.2fr]">
          <div>
            <Wordmark light />
            <p className="mt-5 text-[15px] font-semibold text-white">
              Building technology for what&rsquo;s next.
            </p>
            <p className="mt-3 max-w-[300px] text-sm leading-relaxed text-slate-400">
              We design and engineer modern software, digital products, cloud platforms, and
              intelligent technology solutions.
            </p>
            <div className="mt-6 flex flex-col gap-2.5 text-sm text-slate-400">
              <a
                href={`mailto:${contact.email}`}
                data-testid="footer-email-link"
                className="flex items-center gap-2.5 transition-colors duration-200 hover:text-white"
              >
                <Mail size={14} className="text-slate-500" /> {contact.email}
              </a>
              <a
                href={contact.phoneHref}
                data-testid="footer-phone-link"
                className="flex items-center gap-2.5 transition-colors duration-200 hover:text-white"
              >
                <Phone size={14} className="text-slate-500" /> {contact.phone}
              </a>
              <span className="flex items-center gap-2.5" data-testid="footer-location">
                <MapPin size={14} className="text-slate-500" /> {contact.location}
              </span>
            </div>
            <div className="mt-6 flex items-center gap-3">
              {socials.map((s) => (
                <a
                  key={s.label}
                  href="#"
                  onClick={(e) => e.preventDefault()}
                  aria-label={s.label}
                  data-testid={s.testId}
                  className="grid h-9 w-9 place-items-center rounded-[10px] border border-white/10 text-slate-400 transition-[border-color,color] duration-200 hover:border-blue-500/60 hover:text-white"
                >
                  <s.icon size={15} />
                </a>
              ))}
            </div>
          </div>

          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-slate-500">Company</p>
            <div className="mt-5 flex flex-col gap-3">
              <Link
                to="/about"
                data-testid="footer-about-link"
                className="w-fit text-sm text-slate-300 transition-colors duration-200 hover:text-white"
              >
                About
              </Link>
              <Link
                to="/solutions"
                data-testid="footer-solutions-link"
                className="w-fit text-sm text-slate-300 transition-colors duration-200 hover:text-white"
              >
                Solutions
              </Link>
              <Link
                to="/products"
                data-testid="footer-products-link"
                className="w-fit text-sm text-slate-300 transition-colors duration-200 hover:text-white"
              >
                Products
              </Link>
              <Link
                to="/team"
                data-testid="footer-team-link"
                className="w-fit text-sm text-slate-300 transition-colors duration-200 hover:text-white"
              >
                Our Team
              </Link>
              <Link
                to="/contact"
                data-testid="footer-contact-link"
                className="w-fit text-sm text-slate-300 transition-colors duration-200 hover:text-white"
              >
                Contact
              </Link>
            </div>
          </div>

          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-slate-500">Services</p>
            <div className="mt-5 flex flex-col gap-3">
              {services.map((s) => (
                <Link
                  key={s.slug}
                  to={`/services#${s.slug}`}
                  data-testid={`footer-service-${s.slug}`}
                  className="w-fit text-left text-sm text-slate-300 transition-colors duration-200 hover:text-white"
                >
                  {s.short}
                </Link>
              ))}
            </div>
          </div>

          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-slate-500">Legal</p>
            <div className="mt-5 flex flex-col gap-3">
              {legalLinks.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`footer-legal-${l.to.slice(1)}`}
                  className="w-fit text-sm text-slate-300 transition-colors duration-200 hover:text-white"
                >
                  {l.label}
                </Link>
              ))}
              <button
                type="button"
                data-testid="footer-cookie-preferences"
                onClick={() => window.dispatchEvent(new CustomEvent("bytevora:cookie-preferences"))}
                className="w-fit text-left text-sm text-slate-300 transition-colors duration-200 hover:text-white"
              >
                Cookie Preferences
              </button>
            </div>
          </div>
        </div>

        <div className="mt-14 border-t border-white/[0.08] pt-8">
          <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-slate-500">
            Registered Office
          </p>
          <p className="mt-2.5 max-w-md text-xs leading-relaxed text-slate-500" data-testid="footer-registered-office">
            {contact.registeredOffice}
          </p>
          <p className="mt-2 font-mono text-xs tracking-wide text-slate-500" data-testid="footer-gstin">
            GSTIN: {contact.gst}
          </p>
        </div>

        <div className="mt-8 flex flex-col gap-3 border-t border-white/[0.08] pt-7 text-xs text-slate-500 md:flex-row md:items-center md:justify-between">
          <p data-testid="footer-copyright">
            © 2026 {contact.legalName}. All rights reserved.
          </p>
          <p className="font-mono tracking-wide" data-testid="footer-cin">
            CIN: {contact.cin}
          </p>
        </div>
      </div>
    </footer>
  );
}
